import { createContext, useState } from "react";
import { useDispatch } from "react-redux";

import {
  createProvinceService,
  deleteProvinceService,
  editProvinceService,
  getProvincesService,
  getCitiesService,
  createCityService,
  editCityService,
  deleteCityService,
} from "./../../../services/userServices";
import {
  setLoadingFalseAction,
  setLoadingTrueAction,
} from "./../../../redux/actions/loadingActions/loadingActions";
import {
  toastErrorMessage,
  toastSuccessMessage,
} from "../../../utils/toastMessage/toastMessage";
import { isEmpty } from "lodash";
import { setCitiesAction } from "./../../../redux/actions/citiesActions/citiesActions";

export const provinceAndCityAdminContext = createContext({
  provinces: [],
  setProvinces: () => {},
  cities: [],
  setCities: () => {},
  id: "",
  setId: () => {},
  name: "",
  setName: () => {},
  province: "",
  setProvince: () => {},
  isEnabled: null,
  setIsEnabled: () => {},
  isShowModal: null,
  setIsShowModal: () => {},
  handleShowModal: () => {},
  handleCloseModal: () => {},
  handleGetProvinces: () => {},
  handleCreateProvince: () => {},
  handleEditProvince: () => {},
  handleDeleteProvince: () => {},
  handleGetCities: () => {},
  handleCreateCity: () => {},
  handleEditCity: () => {},
  handleDeleteCity: () => {},
});

const ProvinceAndCityAdminContext = ({ children }) => {
  // Redux Dispatch
  const dispatch = useDispatch();

  const [provinces, setProvinces] = useState([]);
  const [cities, setCities] = useState([]);
  const [id, setId] = useState("");
  const [name, setName] = useState("");
  const [province, setProvince] = useState("");
  const [isEnabled, setIsEnabled] = useState(true);
  const [isShowModal, setIsShowModal] = useState(false);

  const handleResetState = () => {
    setId("");
    setName("");
    setProvince("");
    setIsEnabled(true);
  };

  // Create Province And City Modal Operations
  const handleShowModal = (itemValue) => {
    setIsShowModal(true);
    if (itemValue) {
      setId(itemValue.id);
      setName(itemValue.name);
      if (isEmpty(provinces)) {
        setProvince(itemValue.province);
      } else {
        setProvince(
          provinces?.find((_p) => _p?.id === itemValue.province)?.name
        );
      }
      setIsEnabled(itemValue.isEnabled);
    }
  };
  const handleCloseModal = () => {
    setIsShowModal(false);
    handleResetState();
  };

  //* Start Province
  // Handle Get Provinces Method
  const handleGetProvinces = async (event) => {
    event?.preventDefault();

    try {
      dispatch(setLoadingTrueAction());
      const { status, data } = await getProvincesService();
      if (status === 200) {
        setProvinces(data.list);
      }
    } catch ({ response }) {
      if (response?.status) {
        toastErrorMessage();
      }
    } finally {
      dispatch(setLoadingFalseAction());
    }
  };

  // Handle Create Province Method
  const handleCreateProvince = async (event) => {
    event?.preventDefault();

    const objData = {
      province: name,
    };

    try {
      dispatch(setLoadingTrueAction());
      const { status } = await createProvinceService(objData);
      if (status === 200) {
        toastSuccessMessage("آیتم با موفقیت ایجاد شد");
        handleGetProvinces();
        handleCloseModal();
      }
    } catch ({ response }) {
      if (response?.status) {
        toastErrorMessage();
      }
      dispatch(setLoadingFalseAction());
    }
  };

  // Handle Edit Province Method
  const handleEditProvince = async (event) => {
    event?.preventDefault();

    const objData = {
      id,
      province: name,
    };

    try {
      dispatch(setLoadingTrueAction());
      const { status } = await editProvinceService(objData);
      if (status === 200) {
        toastSuccessMessage("آیتم با موفقیت ویرایش شد");
        handleGetProvinces();
        handleCloseModal();
      }
    } catch ({ response }) {
      if (response?.status) {
        toastErrorMessage();
      }
      dispatch(setLoadingFalseAction());
    }
  };

  // Handle Delete Province Method
  const handleDeleteProvince = async (id) => {
    const objData = {
      id,
    };

    try {
      dispatch(setLoadingTrueAction());
      const { status } = await deleteProvinceService(objData);
      if (status === 200) {
        toastSuccessMessage(`آیتم  ${id} با موفقیت حذف شد`);
        handleGetProvinces();
      }
    } catch ({ response }) {
      if (response?.status) {
        toastErrorMessage();
      }
      dispatch(setLoadingFalseAction());
    }
  };
  //* End Province

  //* Start City
  // Handle Get Cities Method
  const handleGetCities = async (event) => {
    event?.preventDefault();

    try {
      dispatch(setLoadingTrueAction());
      const { status, data } = await getCitiesService();
      if (status === 200) {
        setCities(data.list);

        dispatch(setCitiesAction(data.list));
      }
    } catch ({ response }) {
      if (response?.status) {
        toastErrorMessage();
      }
    } finally {
      dispatch(setLoadingFalseAction());
    }
  };

  // Handle Create City Method
  const handleCreateCity = async (event) => {
    event?.preventDefault();

    const objData = {
      city: name,
      isEnabled,
      province,
    };

    try {
      dispatch(setLoadingTrueAction());
      const { status } = await createCityService(objData);
      if (status === 200) {
        toastSuccessMessage("آیتم با موفقیت ایجاد شد");
        handleGetCities();
        handleCloseModal();
      }
    } catch ({ response }) {
      if (response?.status) {
        toastErrorMessage();
      }
      dispatch(setLoadingFalseAction());
    }
  };

  // Handle Edit City Method
  const handleEditCity = async (event) => {
    event?.preventDefault();

    const objData = {
      id,
      city: name,
      isEnabled,
      province,
    };

    try {
      dispatch(setLoadingTrueAction());
      const { status } = await editCityService(objData);
      if (status === 200) {
        toastSuccessMessage("آیتم با موفقیت ویرایش شد");
        handleGetCities();
        handleCloseModal();
      }
    } catch ({ response }) {
      if (response?.status) {
        toastErrorMessage();
      }
      dispatch(setLoadingFalseAction());
    }
  };

  // Handle Delete City Method
  const handleDeleteCity = async (id) => {
    const objData = {
      id,
    };

    try {
      dispatch(setLoadingTrueAction());
      const { status } = await deleteCityService(objData);
      if (status === 200) {
        toastSuccessMessage(`آیتم  ${id} با موفقیت حذف شد`);
        handleGetCities();
      }
    } catch ({ response }) {
      if (response?.status) {
        toastErrorMessage();
      }
      dispatch(setLoadingFalseAction());
    }
  };
  //* End City

  return (
    <provinceAndCityAdminContext.Provider
      value={{
        provinces,
        setProvinces,
        cities,
        setCities,
        id,
        setId,
        name,
        setName,
        province,
        setProvince,
        isEnabled,
        setIsEnabled,
        isShowModal,
        setIsShowModal,
        handleShowModal,
        handleCloseModal,
        handleGetProvinces,
        handleCreateProvince,
        handleEditProvince,
        handleDeleteProvince,
        handleGetCities,
        handleCreateCity,
        handleEditCity,
        handleDeleteCity,
      }}
    >
      {children}
    </provinceAndCityAdminContext.Provider>
  );
};

export default ProvinceAndCityAdminContext;
